/**
 * The crate list for one phase of one event: check-out before, check-in after.
 *
 * Two paths, because those are the two things that actually happen at a venue.
 * Everything is there, which is one tap. Or something is not, which needs the
 * item marked and a sentence saying what happened. completeMaterialsCheck()
 * refuses the second path without that sentence, and the refusal is shown as
 * written.
 */

import { useState } from 'react'
import {
  completeMaterialsCheck,
  setAllChecklistItems,
  setChecklistItemState,
  type MaterialsCheckWithItems,
} from '../../lib/api'
import { Badge, Card } from '../../components/ui'
import { formatDateTime } from '../../lib/format'
import { useAction } from './coachHooks'
import { ActionFeedback } from './coachUi'
import type { ChecklistItem, ChecklistItemState } from '../../types/db'

const PHASE_TITLE = {
  check_out: 'Materials to collect',
  check_in: 'Materials to return',
} as const

const PHASE_HINT = {
  check_out: 'Check the crate against this list before you leave for the venue.',
  check_in: 'Check the crate back in when the event is over.',
} as const

const STATE_CHOICES: Array<{ value: ChecklistItemState; label: string }> = [
  { value: 'ok', label: 'Fine' },
  { value: 'missing', label: 'Missing' },
  { value: 'damaged', label: 'Damaged' },
]

const STATE_TONE: Record<ChecklistItemState, { bg: string; ink: string }> = {
  pending: { bg: 'var(--surface-3)', ink: 'var(--text-muted)' },
  ok: { bg: 'var(--positive)', ink: '#fff' },
  missing: { bg: 'var(--danger)', ink: '#fff' },
  damaged: { bg: 'var(--warning)', ink: '#fff' },
}

const STATE_LABEL: Record<ChecklistItemState, string> = {
  pending: 'Not checked',
  ok: 'Fine',
  missing: 'Missing',
  damaged: 'Damaged',
}

export function EventMaterialsCard({
  check,
  coachId,
  canSign,
  lockedReason,
  onChanged,
}: {
  check: MaterialsCheckWithItems
  coachId: string
  /** True when this coach may touch this phase right now. */
  canSign: boolean
  /** Why they cannot, shown instead of the controls. */
  lockedReason: string | null
  onChanged: () => void
}) {
  const action = useAction(onChanged)
  const [noteOpen, setNoteOpen] = useState(false)
  const [note, setNote] = useState('')

  const signedOff = check.status !== 'pending'
  const editable = canSign && !signedOff
  const problems = check.items.filter((i) => i.state === 'missing' || i.state === 'damaged')
  const unchecked = check.items.filter((i) => i.state === 'pending')

  function setItem(item: ChecklistItem, state: ChecklistItemState) {
    // Pass the existing note through: omitting it clears whatever is there.
    void action.run(() => setChecklistItemState(item.id, state, item.note, coachId))
  }

  function markAllFine() {
    void action.run(
      () => setAllChecklistItems(check.id, 'ok', coachId),
      'Every item marked fine.',
    )
  }

  function signOffClean() {
    void action.run(
      () =>
        completeMaterialsCheck({
          materialsCheckId: check.id,
          checkedBy: coachId,
          allAccountedFor: true,
        }),
      'Signed off. Nothing else to do here.',
    )
  }

  async function signOffWithNote() {
    const ok = await action.run(
      () =>
        completeMaterialsCheck({
          materialsCheckId: check.id,
          checkedBy: coachId,
          allAccountedFor: false,
          note,
        }),
      'Logged. Gina picks this up from here.',
    )
    if (ok) {
      setNote('')
      setNoteOpen(false)
    }
  }

  return (
    <Card>
      <div className="row row--between">
        <div style={{ minWidth: 0 }}>
          <p className="card__title">{PHASE_TITLE[check.phase]}</p>
          {signedOff ? null : (
            <p className="small muted" style={{ marginTop: 2 }}>
              {PHASE_HINT[check.phase]}
            </p>
          )}
        </div>
        {check.status === 'complete' ? (
          <Badge tone="positive">Signed off</Badge>
        ) : check.status === 'issue' ? (
          <Badge tone="danger">Issue logged</Badge>
        ) : (
          <Badge tone="warning">{check.items.length} items</Badge>
        )}
      </div>

      <ul className="checklist" style={{ marginTop: 'var(--sp-3)' }}>
        {check.items.map((item) => (
          <li
            key={item.id}
            style={
              editable
                ? { display: 'block', paddingTop: 'var(--sp-3)', paddingBottom: 'var(--sp-3)' }
                : undefined
            }
          >
            {editable ? (
              <>
                <div className="row" style={{ gap: 'var(--sp-2)' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
                  <span className="qty">x{item.quantity}</span>
                </div>
                <div
                  className="btn-group"
                  role="group"
                  aria-label={`${item.label} condition`}
                  style={{ marginTop: 'var(--sp-2)', gap: 'var(--sp-2)' }}
                >
                  {STATE_CHOICES.map((choice) => {
                    const active = item.state === choice.value
                    const tone = STATE_TONE[choice.value]
                    return (
                      <button
                        key={choice.value}
                        type="button"
                        className="chip"
                        aria-pressed={active}
                        disabled={action.busy}
                        onClick={() => setItem(item, active ? 'pending' : choice.value)}
                        style={{
                          // A crate is checked one handed, in a car park. These
                          // are thumb sized, not text sized.
                          minHeight: 40,
                          flex: '1 1 0',
                          ...(active
                            ? { background: tone.bg, borderColor: tone.bg, color: tone.ink }
                            : null),
                        }}
                      >
                        {choice.label}
                      </button>
                    )
                  })}
                </div>
                {item.note ? (
                  <p className="faint" style={{ marginTop: 'var(--sp-2)' }}>
                    Note: {item.note}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <span
                  className={`tickbox${item.state === 'ok' ? ' tickbox--ok' : ''}`}
                  aria-hidden="true"
                >
                  +
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  {item.label}
                  {item.state !== 'ok' && item.state !== 'pending' ? (
                    <span
                      className="faint"
                      style={{ display: 'block', color: 'var(--danger-soft-ink)' }}
                    >
                      {STATE_LABEL[item.state]}
                      {item.note ? `. ${item.note}` : ''}
                    </span>
                  ) : null}
                </span>
                <span className="qty">x{item.quantity}</span>
              </>
            )}
          </li>
        ))}
      </ul>

      {signedOff ? (
        <p className="faint" style={{ marginTop: 'var(--sp-3)' }}>
          {check.checked_by === coachId ? 'You signed this off' : 'Signed off'}
          {check.checked_at ? ` on ${formatDateTime(check.checked_at)}` : ''}
          {check.notes ? `. ${check.notes}` : '.'}
        </p>
      ) : !canSign ? (
        <p className="faint" style={{ marginTop: 'var(--sp-3)' }}>
          {lockedReason}
        </p>
      ) : (
        <div style={{ marginTop: 'var(--sp-4)' }}>
          {problems.length === 0 ? (
            <>
              <button
                type="button"
                className="btn btn--block"
                disabled={action.busy}
                onClick={signOffClean}
              >
                {check.phase === 'check_out' ? 'Everything is here' : 'Everything came back'}
              </button>
              <div className="btn-group" style={{ marginTop: 'var(--sp-2)' }}>
                {unchecked.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={action.busy}
                    onClick={markAllFine}
                  >
                    {unchecked.length < check.items.length ? 'Mark the rest fine' : 'Mark all fine'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={action.busy}
                  onClick={() => setNoteOpen((v) => !v)}
                >
                  {noteOpen ? 'Never mind' : 'Something is wrong'}
                </button>
              </div>
            </>
          ) : (
            <p className="small muted">
              {problems.length} {problems.length === 1 ? 'item needs' : 'items need'} a word of
              explanation before this can be signed off.
            </p>
          )}

          {noteOpen || problems.length > 0 ? (
            <div className="field" style={{ marginTop: 'var(--sp-3)', marginBottom: 0 }}>
              <label htmlFor={`materials-note-${check.id}`}>What is missing or damaged?</label>
              <textarea
                id={`materials-note-${check.id}`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="One kickboard did not come back. Left at the pool office."
              />
              <button
                type="button"
                className="btn btn--accent btn--block"
                disabled={action.busy}
                onClick={() => void signOffWithNote()}
              >
                Sign off and flag it
              </button>
            </div>
          ) : null}
        </div>
      )}

      <ActionFeedback error={action.error} done={action.done} />
    </Card>
  )
}
